/**
 * StockTab — Per-warehouse stock rendered with one cell per parent column.
 * Uses shared SubTableRow/SubTableHeadRow components and centralized styling.
 */

import { cn } from "@/lib/utils";
import type { Bin } from "@/lib/erp/types/stock";
import { useTableStyles, SUB_TABLE_DATA_ROW_CLASS, SUB_TABLE_SKELETON_CLASS } from "./useTableStyles";
import { SubTableRow, SubTableHeadRow } from "./SubTableRow";
import { AddStockPopover } from "./AddStockPopover";

const fmt = new Intl.NumberFormat(undefined, {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

interface StockTabProps {
  data: Bin[] | undefined;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
  columnWidths: number[];
  itemCode: string;
}

/** Map child fields to parent column indices. Skip first 3 system cols. */
function getFieldPositions(colCount: number) {
  const start = Math.min(3, colCount - 1);
  return {
    warehouse: start,
    actualQty: Math.min(start + 1, colCount - 1),
    reservedQty: Math.min(start + 2, colCount - 1),
    projectedQty: Math.min(start + 3, colCount - 1),
  };
}

export function StockTab({ data, isLoading, isError, refetch, columnWidths, itemCode }: StockTabProps) {
  const { headCellClass, dataCellClass, headMinHeight, dataMinHeight } = useTableStyles();
  const pos = getFieldPositions(columnWidths.length);

  const headerCells: Record<number, { label: string; align?: "right" }> = {
    [pos.warehouse]: { label: "Warehouse" },
    [pos.actualQty]: { label: "Actual Qty", align: "right" },
    [pos.reservedQty]: { label: "Reserved Qty", align: "right" },
    [pos.projectedQty]: { label: "Projected Qty", align: "right" },
  };

  if (isLoading) {
    const skeleton = <div className={cn("h-3.5 w-14 ml-auto", SUB_TABLE_SKELETON_CLASS)} />;
    const skeletonName = <div className={cn("h-3.5 w-24", SUB_TABLE_SKELETON_CLASS)} />;
    return (
      <div>
        <SubTableHeadRow columnWidths={columnWidths} cells={headerCells} headClass={headCellClass} minHeight={headMinHeight} />
        {[1, 2, 3].map((i) => (
          <SubTableRow
            key={i}
            columnWidths={columnWidths}
            cellClass={dataCellClass}
            minHeight={dataMinHeight}
            className={SUB_TABLE_DATA_ROW_CLASS}
            cells={{
              [pos.warehouse]: skeletonName,
              [pos.actualQty]: skeleton,
              [pos.reservedQty]: skeleton,
              [pos.projectedQty]: skeleton,
            }}
          />
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <div className={cn(dataCellClass, "text-destructive")} style={{ minHeight: dataMinHeight }}>
        Could not load stock data ·{" "}
        <button onClick={refetch} className="underline hover:no-underline">Retry</button>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className={cn(dataCellClass, "text-muted-foreground")} style={{ minHeight: dataMinHeight }}>
        No stock records ·{" "}
        <AddStockPopover itemCode={itemCode} onCreated={refetch} />
      </div>
    );
  }

  const totals = data.reduce(
    (acc, bin) => ({
      actual_qty: acc.actual_qty + (bin.actual_qty ?? 0),
      reserved_qty: acc.reserved_qty + (bin.reserved_qty ?? 0),
      projected_qty: acc.projected_qty + (bin.projected_qty ?? 0),
    }),
    { actual_qty: 0, reserved_qty: 0, projected_qty: 0 },
  );

  const rightAlign = "text-right";

  return (
    <div>
      <SubTableHeadRow columnWidths={columnWidths} cells={headerCells} headClass={headCellClass} minHeight={headMinHeight} />
      {data.map((bin) => (
        <SubTableRow
          key={bin.name}
          columnWidths={columnWidths}
          cellClass={dataCellClass}
          minHeight={dataMinHeight}
          cellExtraClass={{
            [pos.actualQty]: rightAlign,
            [pos.reservedQty]: rightAlign,
            [pos.projectedQty]: rightAlign,
          }}
          className={SUB_TABLE_DATA_ROW_CLASS}
          cells={{
            [pos.warehouse]: <span className="truncate">{bin.warehouse}</span>,
            [pos.actualQty]: <span className="tabular-nums">{fmt.format(bin.actual_qty)}</span>,
            [pos.reservedQty]: <span className="tabular-nums">{fmt.format(bin.reserved_qty)}</span>,
            [pos.projectedQty]: <span className="tabular-nums">{fmt.format(bin.projected_qty)}</span>,
          }}
        />
      ))}
      <SubTableRow
        columnWidths={columnWidths}
        cellClass={dataCellClass}
        minHeight={dataMinHeight}
        cellExtraClass={{
          [pos.actualQty]: rightAlign,
          [pos.reservedQty]: rightAlign,
          [pos.projectedQty]: rightAlign,
        }}
        className="border-b border-neutral-200 dark:border-neutral-700 font-medium bg-neutral-50/50 dark:bg-neutral-900/50"
        cells={{
          [pos.warehouse]: "Total",
          [pos.actualQty]: <span className="tabular-nums">{fmt.format(totals.actual_qty)}</span>,
          [pos.reservedQty]: <span className="tabular-nums">{fmt.format(totals.reserved_qty)}</span>,
          [pos.projectedQty]: <span className="tabular-nums">{fmt.format(totals.projected_qty)}</span>,
        }}
      />
      {/* Footer action zone */}
      <div className={cn(dataCellClass, "text-muted-foreground")} style={{ minHeight: dataMinHeight }}>
        <AddStockPopover itemCode={itemCode} onCreated={refetch} />
      </div>
    </div>
  );
}
